import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, LayoutGrid } from "lucide-react";

// Card photos resolve from /public/images/projects/ first, stock as fallback.
const CARDS = [
  {
    title: "Residential Homes",
    sub: "Custom new home builds, luxury finishes & turnkey living across NSW.",
    cta: "View Residential Homes",
    to: "/services/residential-homes",
    color: "#D500F9",
    local: "card-residential.jpg",
    fallback: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1200",
    caption: "Turn-Key Residential Family Homes",
    testId: "service-card-residential",
  },
  {
    title: "Multi-Unit Developments",
    sub: "Duplexes, townhouses & masterplanned residential communities.",
    cta: "View Multi-Unit Builds",
    to: "/services/multi-unit-developments",
    color: "#FFC107",
    local: "card-multi-unit.jpg",
    fallback: "https://images.pexels.com/photos/1396132/pexels-photo-1396132.jpeg?auto=compress&cs=tinysrgb&w=1200",
    testId: "service-card-multi-unit",
  },
  {
    title: "Childcare Centres",
    sub: "Purpose-built, council-compliant turnkey childcare assets for investors.",
    cta: "View Childcare Projects",
    to: "/services/childcare-centres",
    color: "#FF1744",
    local: "card-childcare.jpg",
    fallback: "https://images.pexels.com/photos/3662667/pexels-photo-3662667.jpeg?auto=compress&cs=tinysrgb&w=1200",
    testId: "service-card-childcare",
  },
  {
    title: "Shop Fit-Outs",
    sub: "High-impact retail, hospitality & commercial interior transformations.",
    cta: "View Shop Fit-Outs",
    to: "/services/commercial-shop-fitouts",
    color: "#00B0FF",
    local: "card-fitouts.jpg",
    fallback: "https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&w=1200",
    caption: "Monstone Commercial Hospitality Fit-Out — Completed",
    testId: "service-card-fitouts",
  },
  {
    title: "Medical Facilities",
    sub: "Specialized healthcare, dental & clinical practice fit-outs.",
    cta: "View Medical Facilities",
    to: "/services/medical-facilities",
    color: "#00E676",
    local: "card-medical.jpg",
    fallback: "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=1200",
    testId: "service-card-medical",
  },
  {
    title: "Knock-Down & Rebuild",
    sub: "Maximize your existing block potential with complete site rebuilding.",
    cta: "View Knock-Down Services",
    to: "/pillars/custom-builds",
    color: "#FF3D00",
    local: "card-kdr.jpg",
    fallback: "https://images.pexels.com/photos/32870/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=1200",
    testId: "service-card-kdr",
  },
];

const CardImg = ({ local, fallback, alt }) => {
  const [src, setSrc] = useState(`/images/projects/${local}`);
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => src !== fallback && setSrc(fallback)}
      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
    />
  );
};

export const ServicesGrid = () => (
  <section id="services" className="relative px-6 sm:px-12 lg:px-20 py-28 lg:py-32 before:absolute before:top-0 before:left-1/2 before:-translate-x-1/2 before:h-px before:w-[90%] before:max-w-7xl before:bg-gradient-to-r before:from-transparent before:via-amber-500/20 before:to-transparent" data-testid="services-grid-section">
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-14"
      >
        <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#FF1744] mb-4 flex items-center gap-3">
          <LayoutGrid size={14} /> WHAT WE DELIVER
        </p>
        <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02] max-w-3xl">
          Our Construction &amp;{" "}
          <em className="font-serif-lux italic font-semibold text-gradient-fire">Development Services.</em>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {CARDS.map((c, i) => (
          <motion.div
            key={c.title}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
          >
            <Link
              to={c.to}
              data-testid={c.testId}
              className="group block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] hover:border-white/25 transition-colors duration-300"
            >
              <div className="relative h-56 overflow-hidden">
                <CardImg local={c.local} fallback={c.fallback} alt={`${c.title} by JAI HOMES`} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-transparent to-transparent" />
                {c.caption && (
                  <span
                    className="absolute bottom-3 left-3 rounded-full glass border border-white/15 px-3 py-1 font-display text-[9px] font-bold tracking-wide text-white/85 pointer-events-none"
                    data-testid={`${c.testId}-caption`}
                  >
                    {c.caption}
                  </span>
                )}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `radial-gradient(70% 90% at 50% 100%, ${c.color}22, transparent 70%)` }}
                />
              </div>
              <div className="p-6 border-t-2" style={{ borderTopColor: c.color }}>
                <h3 className="font-display font-bold text-xl text-white tracking-tight">{c.title}</h3>
                <p className="mt-2 text-xs text-white/55 leading-relaxed min-h-[32px]">{c.sub}</p>
                <span
                  className="mt-4 inline-flex items-center gap-2 font-display text-[13px] font-semibold transition-colors duration-300"
                  style={{ color: c.color }}
                >
                  {c.cta}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
